import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Briefcase, CalendarCheck, ChevronLeft, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export const PaidGigs = () => {
  const { token } = useAuth();
  const [gigs, setGigs] = useState(null);
  const [cal, setCal] = useState(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() };
  });
  const [selected, setSelected] = useState("");

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/my/gigs`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => setGigs(d.gigs || []))
      .catch(() => setGigs([]));
  }, [token]);

  const byDate = useMemo(() => {
    const m = {};
    (gigs || []).forEach((g) => {
      if (g.date) (m[g.date] = m[g.date] || []).push(g);
    });
    return m;
  }, [gigs]);

  if (!gigs || gigs.length === 0) return null;

  const today = new Date().toISOString().slice(0, 10);
  const monthLabel = new Date(cal.y, cal.m, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  const firstDay = new Date(cal.y, cal.m, 1).getDay();
  const daysInMonth = new Date(cal.y, cal.m + 1, 0).getDate();
  const shift = (d) => {
    const nd = new Date(cal.y, cal.m + d, 1);
    setCal({ y: nd.getFullYear(), m: nd.getMonth() });
  };
  const shown = selected ? gigs.filter((g) => g.date === selected) : gigs;

  return (
    <div className="paid-gigs-panel" data-testid="paid-gigs-panel">
      <h3 className="req-title"><Briefcase size={15} /> My paid gigs</h3>
      <p className="gd-upload-note">Locked-in bookings — the booking fee is paid, so these are confirmed.</p>

      <div className="gig-cal" data-testid="gig-calendar">
        <div className="gig-cal-head">
          <button type="button" className="gig-cal-nav" onClick={() => shift(-1)} data-testid="gig-cal-prev">
            <ChevronLeft size={15} />
          </button>
          <span className="gig-cal-month" data-testid="gig-cal-month">{monthLabel}</span>
          <button type="button" className="gig-cal-nav" onClick={() => shift(1)} data-testid="gig-cal-next">
            <ChevronRight size={15} />
          </button>
        </div>
        <div className="gig-cal-grid">
          {WEEKDAYS.map((w, i) => <span key={`w${i}`} className="gig-cal-wd">{w}</span>)}
          {Array.from({ length: firstDay }).map((_, i) => <span key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const d = iso(cal.y, cal.m, i + 1);
            const has = !!byDate[d];
            const cls = [
              "gig-cal-day",
              has ? "has-gig" : "",
              d === today ? "today" : "",
              d === selected ? "sel" : "",
            ].join(" ").trim();
            return (
              <button
                type="button"
                key={d}
                className={cls}
                disabled={!has}
                onClick={() => setSelected(selected === d ? "" : d)}
                data-testid={`gig-cal-day-${d}`}
              >
                {i + 1}
                {has && <span className="gig-cal-dot" />}
              </button>
            );
          })}
        </div>
        {selected && (
          <button type="button" className="gig-cal-clear" onClick={() => setSelected("")} data-testid="gig-cal-clear">
            Showing {selected} — tap to show all
          </button>
        )}
      </div>

      <ul className="paid-gigs-list">
        {shown.map((g) => {
          const upcoming = g.date && g.date >= today;
          return (
            <li className="paid-gig-row" key={g.id} data-testid={`paid-gig-${g.id}`}>
              <span className={`paid-gig-badge ${upcoming ? "up" : "past"}`}>
                {upcoming ? "upcoming" : g.date ? "past" : "paid"}
              </span>
              <span className="paid-gig-label">{g.label}</span>
              {g.client && <span className="paid-gig-client">with {g.client}</span>}
              {g.date && (
                <span className="paid-gig-when">
                  <CalendarCheck size={12} /> {g.date}{g.time ? ` · ${g.time}` : ""}
                </span>
              )}
              {g.amount != null && <span className="paid-gig-amount">${Number(g.amount).toFixed(2)} paid</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
